import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentDriver } from 'src/features/drivers/slice/driverSlice';
import { useUploadDlImageMutation } from 'src/features/drivers/api/driverApi';
import { RootState } from 'src/store/store';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents'; // Import GTM helper method
import { isValidDriverResponse } from '../helpers/driverHelpers'; // Import the helper function

export const useHandleDlImageUpload = (setDlExtractorError: (value: boolean) => void) => {
  const dispatch = useDispatch();
  const [uploadDlImage] = useUploadDlImageMutation();

  const currentDriver = useSelector((state: RootState) => state.drivers.currentDriver);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const [loading, setLoading] = useState(false);

  const handleDlImageUpload = async (selectedImage: string | null) => {
    if (!selectedImage) {
      return;
    }

    const formData = new FormData();
    const imageUri = selectedImage;

    // Create a Blob or file from the selected image (ensure the correct type)
    const imageFile = await fetch(imageUri);
    const blob = await imageFile.blob();
    formData.append('image', blob, 'driver-image.jpg'); // Add the image to the FormData object

    setLoading(true);
    try {
      const driverData = await uploadDlImage(formData).unwrap();

      // Validate the driver response using the helper function
      if (isValidDriverResponse(driverData)) {
        // Track DL Extracted event
        trackGTMEvent(
          'dl-extracted',
          {
            userEvent: 'Driver’s license details successfully extracted from image',
          },
          accountType,
        );
      } else {
        // Handle invalid or incomplete data
        trackGTMEvent(
          'dl-extraction-failed',
          {
            userEvent: 'Unable to extract Driver’s license details from image',
          },
          accountType,
        );
      }

      // Update Redux store with extracted data
      dispatch(
        setCurrentDriver({
          ...currentDriver,
          firstName: driverData.firstName ?? (currentDriver?.firstName || ''),
          lastName: driverData.lastName ?? (currentDriver?.lastName || ''),
          dlNumber: driverData.dlNumber ?? (currentDriver?.dlNumber || ''),
          dlState: driverData.dlState ?? (currentDriver?.dlState || ''),
          maritalStatus: driverData.maritalStatus ?? currentDriver?.maritalStatus,
          yearsOfExperience: driverData.yearsOfExperience ?? currentDriver?.yearsOfExperience,
          monthsOfExperience: driverData.monthsOfExperience ?? currentDriver?.monthsOfExperience,
          dateOfBirth: driverData.dateOfBirth ?? (currentDriver?.dateOfBirth || new Date()),
          dateOfHire: driverData.dateOfHire ?? (currentDriver?.dateOfHire || new Date()),
          eligible: driverData.eligible ?? currentDriver?.eligible,
          accidents: driverData.accidents ?? currentDriver?.accidents,
          violations: driverData.violations ?? currentDriver?.violations,
          suspensions: driverData.suspensions ?? currentDriver?.suspensions,
          majorViolations: driverData.majorViolations ?? currentDriver?.majorViolations,
          active: driverData.active ?? currentDriver?.active,
        }),
      );
    } catch (error) {
      setDlExtractorError(true);
      toggleToastState(setDlExtractorError);
      console.error('Error uploading image:', error);
    } finally {
      setLoading(false);
    }
  };

  return { handleDlImageUpload, loading };
};
